import {
  BookingStatusEnum,
  AgencyInvoiceStatusEnum,
  OtherNotificationTriggerEnum,
} from './types';
import { sleep, randomString } from '@/utils/index';
import request from '@/utils/request';

// data type
export namespace get_octb_setting {
  export interface Request {}

  export interface BankAccount {
    bankNumber: string;
    bankName: string;
    branchNumber: string;
    branchName: string;
    accountNumber: string;
    accountName: string;
  }

  export interface BookingStatusChangeNotificationDestination {
    status: BookingStatusEnum;
    emails: string[];
  }

  export interface AgencyInvoiceStatusChangeNotificationDestination {
    status: AgencyInvoiceStatusEnum;
    emails: string[];
  }

  export interface OtherNotificationTriggerDestination {
    trigger: OtherNotificationTriggerEnum;
    emails: string[];
  }

  export interface Response {
    bankAccounts: BankAccount[];
    bookingNotificationDestinations: BookingStatusChangeNotificationDestination[];
    agencyInvoiceNotificationDestinations: AgencyInvoiceStatusChangeNotificationDestination[];
    otherNotificationTriggerDestinations: OtherNotificationTriggerDestination[];
    senderEmail: string;
    businessCodes: string[];
    accountCodes: string[];
    auxiliaryCodes: string[];
  }
}

export namespace update_octb_setting {
  export interface BankAccount {
    bankNumber: string;
    bankName: string;
    branchNumber: string;
    branchName: string;
    accountNumber: string;
    accountName: string;
  }

  export interface BookingStatusChangeNotificationDestination {
    status: BookingStatusEnum;
    emails: string[];
  }

  export interface AgencyInvoiceStatusChangeNotificationDestination {
    status: AgencyInvoiceStatusEnum;
    emails: string[];
  }

  export interface OtherNotificationTriggerDestination {
    trigger: OtherNotificationTriggerEnum;
    emails: string[];
  }

  export interface Request {
    bankAccounts: BankAccount[];
    bookingNotificationDestinations: BookingStatusChangeNotificationDestination[];
    agencyInvoiceNotificationDestinations: AgencyInvoiceStatusChangeNotificationDestination[];
    otherNotificationTriggerDestinations: OtherNotificationTriggerDestination[];
    senderEmail: string;
    businessCodes: string[];
    accountCodes: string[];
    auxiliaryCodes: string[];
  }

  export interface Response {}
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';

const mockBankAccountsCount = 2;

const mockBankAccount = (hash: number) => {
  const num = hash >= 0 ? hash : -hash;
  return {
    bankNumber: `dummyBankNumber${num}`,
    bankName: `dummyBankName${num}`,
    branchNumber: `dummyBranchNumber${num}`,
    branchName: `dummyBranchName${num}`,
    accountNumber: `dummyAccountNumber${num}`,
    accountName: `dummyAccountName${num}`,
  } as get_octb_setting.BankAccount;
};

const mockBankAccounts = () => {
  const bankAccounts: get_octb_setting.BankAccount[] = [];
  for (let i = 0; i < mockBankAccountsCount; i++) {
    bankAccounts.push(mockBankAccount(i));
  }
  return bankAccounts;
};

export const mockBackAccountsCache = mockBankAccounts();

const mockBookingNotificationDestinations = () => {
  const bookingNotificationDestinations: get_octb_setting.BookingStatusChangeNotificationDestination[] = [];
  Object.values(BookingStatusEnum).forEach(s => {
    if ((s as BookingStatusEnum) !== BookingStatusEnum.UNKNOWN_BOOKING_STATUS) {
      bookingNotificationDestinations.push({
        status: s,
        emails: [`notify-${s}@octb.com`],
      });
    }
  });
  return bookingNotificationDestinations;
};

export const mockBookingNotificationDestinationsCache = mockBookingNotificationDestinations();

const mockAgencyInvoiceNotificationDestinations = () => {
  const agencyInvoiceNotificationDestinations: get_octb_setting.AgencyInvoiceStatusChangeNotificationDestination[] = [];
  Object.values(AgencyInvoiceStatusEnum).forEach(s => {
    if (
      (s as AgencyInvoiceStatusEnum) !==
      AgencyInvoiceStatusEnum.UNKNOWN_INVOICE_STATUS
    ) {
      agencyInvoiceNotificationDestinations.push({
        status: s,
        emails: [`notify-${s}@octb.com`],
      });
    }
  });
  return agencyInvoiceNotificationDestinations;
};

export const mockAgencyInvoiceNotificationDestinationsCache = mockAgencyInvoiceNotificationDestinations();

const mockOtherNotificationDestinations = () => {
  const otherNotificationDestinations: get_octb_setting.OtherNotificationTriggerDestination[] = [];
  Object.values(OtherNotificationTriggerEnum).forEach(t => {
    if (
      (t as OtherNotificationTriggerEnum) !==
      OtherNotificationTriggerEnum.UNKNOWN_TRIGGER
    ) {
      otherNotificationDestinations.push({
        trigger: t,
        emails: [`notify-${t}@octb.com`],
      });
    }
  });
  return otherNotificationDestinations;
};

export const mockOtherNotificationDestinationsCache = mockOtherNotificationDestinations();

export let mockSenderEmailCache = 'dummy@example.com';

export const mockBusinessCodesCache: string[] = [];
export const mockAccountCodesCache: string[] = [];
export const mockAuxiliaryCodesCache: string[] = [];

const mockGetOctbSetting = (req?: get_octb_setting.Request) => {
  return {
    bankAccounts: Object.assign([], mockBackAccountsCache),
    bookingNotificationDestinations: Object.assign(
      [],
      mockBookingNotificationDestinationsCache
    ),
    agencyInvoiceNotificationDestinations: Object.assign(
      [],
      mockAgencyInvoiceNotificationDestinationsCache
    ),
    otherNotificationTriggerDestinations: Object.assign(
      [],
      mockOtherNotificationDestinationsCache
    ),
    senderEmail: mockSenderEmailCache,
    businessCodes: Object.assign([], mockBusinessCodesCache),
    accountCodes: Object.assign([], mockAccountCodesCache),
    auxiliaryCodes: Object.assign([], mockAuxiliaryCodesCache),
  } as get_octb_setting.Response;
};

const mockUpdateOctbSetting = (req: update_octb_setting.Request) => {
  mockBackAccountsCache.splice(0);
  req.bankAccounts.forEach(b => {
    mockBackAccountsCache.push(b);
  });
  mockBookingNotificationDestinationsCache.splice(0);
  req.bookingNotificationDestinations.forEach(d => {
    mockBookingNotificationDestinationsCache.push(d);
  });
  mockAgencyInvoiceNotificationDestinationsCache.splice(0);
  req.agencyInvoiceNotificationDestinations.forEach(d => {
    mockAgencyInvoiceNotificationDestinationsCache.push(d);
  });
  mockOtherNotificationDestinationsCache.splice(0);
  req.otherNotificationTriggerDestinations.forEach(d => {
    mockOtherNotificationDestinationsCache.push(d);
  });
  mockSenderEmailCache = req.senderEmail;
  mockBusinessCodesCache.splice(0);
  req.businessCodes.forEach(c => {
    mockBusinessCodesCache.push(c);
  });
  mockAccountCodesCache.splice(0);
  req.accountCodes.forEach(c => {
    mockAccountCodesCache.push(c);
  });
  mockAuxiliaryCodesCache.splice(0);
  req.auxiliaryCodes.forEach(c => {
    mockAuxiliaryCodesCache.push(c);
  });
  return {} as update_octb_setting.Response;
};

// api
export const getOctbSetting = async (req?: get_octb_setting.Request) => {
  if (useMock) {
    await sleep(100);
    return mockGetOctbSetting(req);
  }

  const res = await request({
    url: '/v1/get-octb-setting',
    method: 'post',
    data: req,
  });
  return res.data as get_octb_setting.Response;
};

// api
export const updateOctbSetting = async (req: update_octb_setting.Request) => {
  if (useMock) {
    await sleep(100);
    return mockUpdateOctbSetting(req);
  }

  const res = await request({
    url: '/v1/update-octb-setting',
    method: 'post',
    data: req,
  });
  return res.data as update_octb_setting.Response;
};
